import styled from 'styled-components';
import { SIZES } from '../../fundamentals/sizes/sizes';
import { IGutterAsMarginProps } from '../../fundamentals/spaces/space';
import { useTestId } from '../../hooks/useTestId/useTestId';
import { BASIC_COLOURS, BASE_STATUS_COLOURS, BRAND_COLOURS } from '../../fundamentals/colours/variants';
import { useFilterColourCss } from '../../fundamentals/colours/filter';
import { useAssetIconUrl, useIconBaseCss } from '../Icon/Icon';
import { MATERIAL_ICONS } from '../Icon/icons';
import { ColourAssignment, IColoursCollection } from '../../fundamentals/colours/colourInterfaces';
import { BaseImg } from '../../common/BaseElements';

export const DOT_ICONS_VARIANTS = {
  ...BASE_STATUS_COLOURS,
  ...BASIC_COLOURS,
  ...BRAND_COLOURS,
  YELLOW: 'yellow'
} as const;

export type DotIconVariant = (typeof DOT_ICONS_VARIANTS)[keyof typeof DOT_ICONS_VARIANTS];

type DotIconColours = {
  dotIconColours: Record<DotIconVariant, ColourAssignment>;
};

export const dotIconColours = ({ palette, base, accent, primary, misc }: IColoursCollection): DotIconColours => ({
  dotIconColours: {
    [DOT_ICONS_VARIANTS.SUCCESS]: {
      background: palette.success
    },
    [DOT_ICONS_VARIANTS.ERROR]: {
      background: palette.error
    },
    [DOT_ICONS_VARIANTS.WARNING]: {
      background: palette.warning
    },
    [DOT_ICONS_VARIANTS.INFO]: {
      background: palette.info
    },
    [DOT_ICONS_VARIANTS.SECONDARY]: {
      background: palette.neutral300
    },
    [DOT_ICONS_VARIANTS.BLACK]: {
      background: base.black.colour
    },
    [DOT_ICONS_VARIANTS.WHITE]: {
      background: base.white.colour
    },
    [DOT_ICONS_VARIANTS.PRIMARY]: {
      background: primary.colour
    },
    [DOT_ICONS_VARIANTS.ACCENT]: {
      background: accent.colour
    },
    [DOT_ICONS_VARIANTS.YELLOW]: {
      background: misc.yellow
    }
  }
});

export type DotIconSize =
  | typeof SIZES.XX_MICRO
  | typeof SIZES.X_MICRO
  | typeof SIZES.MICRO
  | typeof SIZES.XXX_SMALL
  | typeof SIZES.XX_SMALL
  | typeof SIZES.X_SMALL
  | typeof SIZES.SMALL
  | typeof SIZES.MEDIUM
  | typeof SIZES.LARGE
  | typeof SIZES.X_LARGE;

export interface IDotIconProps extends IGutterAsMarginProps {
  size?: DotIconSize;
  variant?: DotIconVariant;
}

const DEFAULT_DOT_ICON_SIZE = SIZES.MEDIUM;

export const DotIcon = styled(BaseImg).attrs<IDotIconProps>(({ size = DEFAULT_DOT_ICON_SIZE, variant = DOT_ICONS_VARIANTS.SECONDARY, ...rest }) => {
  const { generateTestId } = useTestId(rest, `DotIcon`);
  const src = useAssetIconUrl(MATERIAL_ICONS.Circle);

  return {
    src,
    size,
    variant,
    ...rest,
    ...generateTestId(variant)
  };
})<IDotIconProps>`
  ${useIconBaseCss};
  ${({ theme, variant }) => useFilterColourCss(theme.dotIconColours[variant!].background)}
`;
